import React, { useCallback, useState, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { extend } from '@thd-nucleus/data-sources';
import { ExperienceContext } from '@thd-nucleus/experience-context';
import { Checkbox, ToggleSwitch } from '@thd-olt-component-react/checkbox';
import { StoreSelector } from '../StoreSelector/StoreSelector';
import '../../styles/refinement.scss';

const StandardRefinement = ({
  checkboxClassName,
  dimension,
  id,
  onChange,
  refinement,
  enableMultiStore,
  toggleSwitch,
  onMultiStoreSelectionChange,
  multiStoreSelection,
  onDisplay
}) => {

  const { selected } = refinement;

  const { channel } = useContext(ExperienceContext);

  const [multiStoreIds, setMultiStoreIds] = useState(multiStoreSelection ? [multiStoreSelection] : []);

  const isMobile = channel === 'mobile';

  const handleOnChange = (event) => {
    const copiedRefinement = { ...refinement };
    const { checked } = event.target;
    if (enableMultiStore && checked && multiStoreIds && refinement.label.toUpperCase() === 'PICK UP TODAY') {
      copiedRefinement.url = `${copiedRefinement.url}&storeSelection=${multiStoreIds.join()}`;
    }
    const [pathName, query = ''] = copiedRefinement.url.split('?');
    const queryParams = query.split('&').filter((param) => param);

    if (onChange) {
      onChange({ refinement: copiedRefinement, dimension, onDisplay });
    }
  };

  const handleStoreChange = (newMultiStoreIds) => {
    const copiedRefinement = { ...refinement };
    copiedRefinement.url = `${copiedRefinement.url}&storeSelection=${newMultiStoreIds.join()}`;

    const [pathName, query = ''] = copiedRefinement.url.split('?');
    const queryParams = query.split('&').filter((param) => param);

    if (onChange) {
      onChange({ refinement: copiedRefinement, dimension, onDisplay });
    }
  };

  const multiStoreCallback = ({ stores }) => {
    const idCount = multiStoreIds?.length;
    const checkedMultiStoreIds = stores.filter((store) => store.checked)
      .map((checkedStore) => parseInt(checkedStore.storeId, 10));

    // Only apply changes to mobile web, not affect the desktop
    if (!isMobile && checkedMultiStoreIds?.length < idCount && !selected) {
      handleStoreChange(checkedMultiStoreIds);
    }

    // When open the dropdown at the first time, both "multiStoreIds" and "checkedMultiStoreIds" are empty
    // Should apply the filter even both of them are empty in mobile web
    if (isMobile && checkedMultiStoreIds?.length <= idCount && !selected) {
      handleStoreChange(checkedMultiStoreIds);
    }
    if (selected && enableMultiStore && onMultiStoreSelectionChange) {
      onMultiStoreSelectionChange({ multiStoreIds, onDisplay });
    }
    setMultiStoreIds(checkedMultiStoreIds);
  };

  const handleOnChangeLink = (event) => {
    event.preventDefault();
    if (onChange) {
      onChange({ refinement, dimension, onDisplay });
    }
  };

  const generateRefinementLabel = (labelClassName) => {
    const displayLabel = refinement?.label?.toUpperCase() === 'PICK UP TODAY'
      ? 'In Stock at Store Today'
      : refinement?.label;

    return (
      <>
        <h3 className={labelClassName}>{displayLabel}</h3> {refinement.recordCount > 0 && `(${refinement.recordCount})`}
      </>
    );
  };

  const labelClassName = classNames('refinement__link', {
    'refinement__link--special-value': refinement.label === 'Special Values'
  });

  return (
    <>
      {!toggleSwitch && (
        <Checkbox
          className={checkboxClassName}
          disabled={parseInt(refinement.recordCount, 10) === 0}
          id={id}
          key={id}
          data-refinement={refinement.label}
          data-refinementvalue={refinement.label}
          data-active="yes"
          data-omni="true"
          onChange={handleOnChange}
          checked={!!selected}
          value={refinement.refinementKey || ''}
        >
          <a
            className="refinement__link"
            href={refinement.url}
            onClick={(event) => handleOnChangeLink(event, refinement)}
          >
            {generateRefinementLabel(labelClassName)}
          </a>
        </Checkbox>
      )}
      {toggleSwitch && (
        <ToggleSwitch
          className="refinement__toggle-switch"
          id={id}
          key={id}
          onChange={handleOnChange}
          checked={!!selected}
        >
          <div className={labelClassName}>In Stock Only</div>
        </ToggleSwitch>
      )}
      {refinement.label.toUpperCase() === 'PICK UP TODAY' && enableMultiStore && (
        <div className="refinement__store-selector">
          <StoreSelector multiStoreCallback={multiStoreCallback} multiStoreSelection={multiStoreSelection} />
        </div>
      )}
    </>
  );
};

StandardRefinement.propTypes = {
  checkboxClassName: PropTypes.string,
  dimension: PropTypes.shape({}),
  id: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  enableMultiStore: PropTypes.bool,
  multiStoreSelection: PropTypes.string,
  onMultiStoreSelectionChange: PropTypes.func,
  refinement: PropTypes.shape({
    label: PropTypes.string,
    recordCount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    refinementKey: PropTypes.string,
    selected: PropTypes.bool,
    url: PropTypes.string,
  }),
  toggleSwitch: PropTypes.bool,
  onDisplay: PropTypes.string
};

StandardRefinement.defaultProps = {
  checkboxClassName: null,
  dimension: null,
  id: null,
  enableMultiStore: false,
  multiStoreSelection: null,
  onMultiStoreSelectionChange: null,
  refinement: null,
  toggleSwitch: false,
  onDisplay: 'false'
};

StandardRefinement.displayName = 'StandardRefinement';

StandardRefinement.dataModel = extend(StoreSelector);

export { StandardRefinement };
